import {
  Box,
  Flex,
  FlexProps,
  Icon,
  StyleProps,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";

import {
  IconButton,
  IconPlayMobile,
  PlaybackRateMenu,
  SkipBackward15MobileIcon,
  SkipForward15MobileIcon,
} from "../../../components";
import CurrentTime from "../MediaPlayer/CurrentTime";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import { VideoStateControl } from "../MediaPlayer/useVideoState";
import { AudioControlsV2 } from "./AudioControlsV2";
import {
  CONTROL_ACTIVE_BG,
  CONTROL_HOVER_BG,
  IconHideVideo,
  IconPause,
} from "./theme";
import VideoFullscreenControlsV2 from "./VideoFullscreenControlsV2";
import VideoPlayerProgressBarV2, {
  VideoPlayerProgressBarV2Props,
} from "./VideoPlayerProgressBarV2";

type VideoControlsMobileV2Props = {
  player: MediaPlayerInterface;
  fullScreenControls?: VideoStateControl;
  pipControls?: VideoStateControl;
  progressBarProps: VideoPlayerProgressBarV2Props;
  onClick?: FlexProps["onClick"];
  variant?: "compact";
  hideVideo?: () => void;
} & StyleProps;

const VideoControlsMobileV2: React.FC<VideoControlsMobileV2Props> = ({
  player,
  pipControls,
  fullScreenControls,
  progressBarProps,
  onClick,
  variant,
  hideVideo,
  ...styles
}) => {
  const {
    time,
    duration: playerDuration,
    setVolume,
    mute,
    playing,
    volume,
    muted,
    playbackRate,
    setPlaybackRate,
  } = player;

  const compact = variant === "compact";

  return (
    <Flex
      direction="column"
      position="absolute"
      top="0"
      width="100%"
      height="100%"
      pt="2"
      px="4"
      pb="3"
      bg="blackAlpha.600"
      fontSize="sm"
      fontWeight="medium"
      onClick={onClick}
      {...styles}
    >
      <Flex>
        <Flex alignItems="center" zIndex={10}>
          <PlaybackRateMenu
            playbackRate={playbackRate}
            setPlaybackRate={setPlaybackRate}
            menuProps={{ variant: "blackAlpha" }}
            menuButtonProps={{
              borderRadius: "full",
              background: "transparent",
            }}
          />

          <AudioControlsV2
            setVolume={setVolume}
            mute={mute}
            volume={volume}
            muted={muted}
            isTop
          />
        </Flex>

        <Flex ml="auto" alignItems="center" gap="1">
          <VideoFullscreenControlsV2
            pipControls={pipControls}
            fullScreenControls={fullScreenControls}
            isSmallScreen
          />
          {hideVideo && (
            <Tooltip label="Hide video">
              <IconButton
                icon={<Icon as={IconHideVideo} boxSize="6" />}
                aria-label="Hide video"
                w={10}
                h={10}
                minW={10}
                borderRadius="full"
                color="white"
                bg="transparent"
                _hover={{ bg: CONTROL_HOVER_BG }}
                _active={{ bg: CONTROL_ACTIVE_BG }}
                onClick={(e) => {
                  e.stopPropagation();
                  hideVideo();
                }}
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>

      <Flex flexGrow="1" alignItems="stretch" color="white">
        {/* Left region - tap to rewind 15s */}
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            player.skipBack(15);
          }}
        >
          <Box pos="relative" display="inline-flex">
            <SkipBackward15MobileIcon size="31" color="white" />
            <Box
              as="span"
              color="white"
              position="absolute"
              bottom="-8px"
              left="-4px"
              fontSize="14px"
              fontWeight="700"
              lineHeight="21px"
              zIndex={1}
            >
              15
            </Box>
          </Box>
        </Flex>

        {/* Center region - tap to play/pause */}
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (playing) {
              player.pause();
            } else {
              player.play();
            }
          }}
        >
          {playing ? (
            <IconPause size={48} color="white" />
          ) : (
            <IconPlayMobile size={48} color="white" />
          )}
        </Flex>

        {/* Right region - tap to fast-forward 15s */}
        <Flex
          flex="1"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            player.skipForward(15);
          }}
        >
          <Box pos="relative" display="inline-flex">
            <SkipForward15MobileIcon size="31" color="white" />
            <Box
              as="span"
              color="white"
              position="absolute"
              bottom="-8px"
              right="-4px"
              fontSize="14px"
              fontWeight="700"
              lineHeight="21px"
              zIndex={1}
            >
              15
            </Box>
          </Box>
        </Flex>
      </Flex>

      <Box mt={compact ? "0" : "auto"} alignSelf="flex-end" w="100%" px={1}>
        <CurrentTime
          time={time}
          duration={playerDuration}
          mb={compact ? "-1" : "0"}
        />
        <Flex
          h={compact ? undefined : "36px"}
          pb="1"
          mb={-1}
          justifyContent="center"
          alignItems="center"
        >
          <VideoPlayerProgressBarV2 {...progressBarProps} />
        </Flex>
      </Box>
    </Flex>
  );
};

export { VideoControlsMobileV2 };
